import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { FireIcon, SparkleIcon, StarIcon } from '../components/Icons'
import './Team.css'

const founders = [
  {
    name: 'Biraja Prasad Rout',
    role: 'Founder',
    responsibility: 'Strategy, Product Design and R&D',
    background: 'IT Professional from Infosys. Bootstrapped Biggies Burger in 2011, went full-time in 2016.',
    image: '/team/Biraja-Sir.webp',
    imageClass: 'founder-image--biraja'
  },
  {
    name: 'Abilash Bellur',
    role: 'Co-Founder',
    responsibility: 'Operations, Franchise Store Operations, Audit, Supply Chain',
    background: '10+ years in apparel retail. Early franchise of Biggies Burger, joined founding team in 2016.',
    image: '/team/Abilash.webp',
    imageClass: 'founder-image--abilash'
  },
  {
    name: 'Sandeep Satapathy',
    role: 'Co-Founder',
    responsibility: 'Business Development, New Franchise Appointment, Expansion',
    background: '9+ years in apparel retail. Early franchise of Biggies Burger, joined in 2016.',
    image: '/team/Sandeep-sir-1-scaled.webp',
    imageClass: 'founder-image--sandeep'
  }
]

const leadership = [
  { name: 'Madhu K Nair', role: 'Director BD & NSO', image: '/team/Madhu-Sir.webp' },
  { name: 'Sunil Mishra', role: 'Brand Head', image: '/team/Sunil-Mishra-scaled.webp' }
]

const cultureValues = [
  { value: 'Bold Flavours, Bolder People', icon: <FireIcon size={35} /> },
  { value: 'Clean Kitchen, Clean Culture', icon: <SparkleIcon size={30} /> },
  { value: 'Respect. Hustle. Team Spirit.', icon: <StarIcon size={30} color="#E4002B" /> }
]

export default function Team() {
  const pageRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.team-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Founder cards animation
      gsap.fromTo('.founder-card',
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.founders-section',
            start: 'top 70%',
          }
        }
      )

      // Leadership animation
      gsap.fromTo('.leader-card',
        { opacity: 0, scale: 0.9 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.leadership-section',
            start: 'top 70%',
          }
        }
      )

      // Culture values animation
      gsap.fromTo('.culture-value',
        { opacity: 0, x: -30 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.culture-section',
            start: 'top 70%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div className="team-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="team-hero">
        <div className="container">
          <div className="team-hero-content">
            <span className="hero-label">Our Team</span>
            <h1 className="hero-title">
              The Minds Behind
              <span className="highlight"> BIGGUYS</span>
            </h1>
            <p className="hero-description">
              Meet the passionate people who are working tirelessly to bring you 
              the best fried chicken experience in India.
            </p>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="founders-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Leadership</span>
            <h2 className="section-title">Our Founding Team</h2>
          </div>

          <div className="founders-grid">
            {founders.map((founder, index) => (
              <div className="founder-card" key={index}>
                <div className={`founder-image ${founder.imageClass || ''}`}>
                  <img src={founder.image} alt={founder.name} loading="lazy" />
                </div>
                <div className="founder-info">
                  <h3 className="founder-name">{founder.name}</h3>
                  <span className="founder-role">{founder.role}</span>
                  <p className="founder-responsibility">{founder.responsibility}</p>
                  <p className="founder-background">{founder.background}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="leadership-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Key Members</span>
            <h2 className="section-title">Leadership Team</h2>
          </div>

          <div className="leadership-grid">
            {leadership.map((leader, index) => (
              <div className="leader-card" key={index}>
                <div className="leader-image">
                  <img src={leader.image} alt={leader.name} loading="lazy" />
                </div>
                <h3 className="leader-name">{leader.name}</h3>
                <span className="leader-role">{leader.role}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="culture-section">
        <div className="container">
          <div className="culture-grid">
            <div className="culture-content">
              <span className="section-label">Our Culture</span>
              <h2 className="section-title">What We Stand For</h2>
              <p>
                At BIGGUYS, our team is driven by shared values that define 
                how we work, serve, and grow together.
              </p>
            </div>
            <div className="culture-values">
              {cultureValues.map((item, index) => (
                <div className="culture-value" key={index}>
                  <span className="value-icon">{item.icon}</span>
                  <span className="value-text">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="team-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Want to Join Our Team?</h2>
            <p>We're always looking for passionate people to join the BIGGUYS family.</p>
            <a href="/careers" className="btn btn-primary">View Open Positions</a>
          </div>
        </div>
      </section>
    </div>
  )
}
